import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ww-admin-token';

const AdminAuthContext = createContext(null);

// One global axios interceptor that injects the admin token on every /api/* request.
let interceptorId = null;
function installInterceptor(token) {
  if (interceptorId !== null) {
    axios.interceptors.request.eject(interceptorId);
    interceptorId = null;
  }
  if (!token) return;
  interceptorId = axios.interceptors.request.use((config) => {
    if (config.url?.includes('/api/')) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || null;
    } catch {
      return null;
    }
  });
  const [status, setStatus] = useState('checking'); // 'checking' | 'in' | 'out'

  // Validate the token on mount (and any time it changes)
  useEffect(() => {
    installInterceptor(token);
    if (!token) {
      setStatus('out');
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/admin/session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (cancelled) return;
        setStatus(data.authenticated ? 'in' : 'out');
        if (!data.authenticated) {
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('out');
      });
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (password) => {
    const { data } = await axios.post(`${API}/admin/login`, { password });
    try { localStorage.setItem(TOKEN_KEY, data.token); } catch { /* ignore */ }
    setToken(data.token);
    setStatus('in');
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/admin/logout`); } catch { /* ignore */ }
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setToken(null);
    setStatus('out');
  }, []);

  return (
    <AdminAuthContext.Provider value={{ status, login, logout, token }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  return ctx;
}
